const MAX_MESSAGE_LENGTH = 2000
const MAX_SINGLE_FILE_SIZE = 200 * 1024 ** 2 // 200MB
const MAX_TOTAL_FILE_SIZE = 500 * 1024 ** 2 // 500MB
const MAX_SINGLE_MESSAGE_FILE_COUNT = 5 // 1メッセージあたりのファイル上限数
const MAX_TOTAL_FILE_COUNT = 15
const MAX_FILE_NAME_LENGTH = 100 // 拡張子のぞく

export const ACCEPTABLE_EXTENSIONS = [
  'png',
  'jpeg',
  'jpg',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'dcm',
  'zip',
]

const mb = (byte: number) => {
  return byte / 1024 ** 2
}

const getFileNameWithoutExtension = (fileName: string) => {
  return fileName.split('.').slice(0, -1).join('.')
}

const getExtension = (fileName: string) => {
  if (!fileName.includes('.')) {
    return ''
  }
  return fileName.split('.').pop() ?? ''
}

const isValidFileName = (fileName: string) => {
  return !/[¥\\/:*?"<>|]/.test(fileName)
}

export const validateMessageLength = (message: string): string | null => {
  if (message.length > MAX_MESSAGE_LENGTH) {
    return `文字数を超過しています。(-${message.length - MAX_MESSAGE_LENGTH}文字)`
  }
  return null
}

const validateFileSize = (file: File): string | null => {
  if (file.size > MAX_SINGLE_FILE_SIZE) {
    return `ファイル上限サイズ(${mb(MAX_SINGLE_FILE_SIZE)}MB)を超えています。`
  }
  return null
}

const validateFileNameLength = (file: File): string | null => {
  const fileName = getFileNameWithoutExtension(file.name)
  if (fileName.length > MAX_FILE_NAME_LENGTH) {
    return `ファイル名は拡張子を除き${MAX_FILE_NAME_LENGTH}文字以内にしてください。`
  }
  return null
}

const validateFileNameChars = (file: File): string | null => {
  if (!isValidFileName(file.name)) {
    return 'ファイル名に¥/:*?"<>|の文字は使用できません。'
  }
  return null
}

const validateSingleMessageFileCount = (
  singleMessageFileCount: number,
): string | null => {
  if (singleMessageFileCount > MAX_SINGLE_MESSAGE_FILE_COUNT) {
    return `一度に送信可能なファイル上限数${MAX_SINGLE_MESSAGE_FILE_COUNT}件を超えています。`
  }
  return null
}

const validateTotalFileCount = (totalFileCount: number): string | null => {
  if (totalFileCount > MAX_TOTAL_FILE_COUNT) {
    return `問い合わせあたりのファイル上限数${MAX_TOTAL_FILE_COUNT}を超えています。\n「問い合わせ資料」よりアップロード済みのファイルを削除してから再度お試しください。`
  }
  return null
}

const validateTotalFileSize = (totalFileSize: number): string | null => {
  if (totalFileSize > MAX_TOTAL_FILE_SIZE) {
    return `問い合わせあたりのファイル上限サイズ${mb(MAX_TOTAL_FILE_SIZE)}MBを超えています。\n「問い合わせ資料」よりアップロード済みのファイルを削除してから再度お試しください。`
  }
  return null
}

export const validateInquiryMessage = (
  message: string,
  newlyUploadedFiles: File[],
  singleMessageFileCount: number,
  totalFileCount: number,
  totalFileSize: number,
) => {
  const errorMessages: string[] = []

  // メッセージのバリデーション
  const messageError = validateMessageLength(message)
  if (messageError) {
    errorMessages.push(messageError)
  }

  // アップロードファイルのバリデーション
  const fileValidators = [
    validateFileSize,
    validateFileNameLength,
    validateFileNameChars,
  ]
  // ファイル毎のバリデーション結果を収集（重複排除）
  const fileErrors = new Set<string>()
  newlyUploadedFiles.forEach(file => {
    fileValidators.forEach(validator => {
      const error = validator(file)
      if (error) {
        fileErrors.add(error)
      }
    })
  })

  errorMessages.push(...fileErrors)

  // メッセージあたりのファイル数のバリデーション
  const singleMessageFileCountError = validateSingleMessageFileCount(
    singleMessageFileCount,
  )
  if (singleMessageFileCountError) {
    errorMessages.push(singleMessageFileCountError)
  }

  // 全ファイルに関するバリデーション
  const totalFileCountError = validateTotalFileCount(totalFileCount)
  if (totalFileCountError) {
    errorMessages.push(totalFileCountError)
  }

  const totalFileSizeError = validateTotalFileSize(totalFileSize)
  if (totalFileSizeError) {
    errorMessages.push(totalFileSizeError)
  }

  return errorMessages
}

export const validateFileExtension = (file: File): boolean => {
  const extension = getExtension(file.name).toLowerCase()
  // DICOMファイルを想定して、拡張子がない場合は許容
  if (!extension) return true
  return ACCEPTABLE_EXTENSIONS.includes(extension)
}
